import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, CheckCircle2, ClipboardList } from "lucide-react";
import { MILESTONE_STATUS, TONE, fmtDate, rank } from "./programUtils";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { TaskStatusSelect } from "@/components/tasks/TaskStatusSelect";

export function StatusPill({ status }) {
  const [label, tone] = MILESTONE_STATUS[status] || [status, "slate"];
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${TONE[tone]}`}>{label}</span>;
}

function QuickCheck({ r, onChanged }) {
  const [blocked, setBlocked] = useState(null);
  const done = r.task?.status === "completed";
  if (!r.task) return <span className="w-4" />;
  if (!done && r.gated) return <span className="shrink-0 text-[11px] text-slate-400 w-36" title={r.gate_reason || ""} data-testid={`req-complete-${r.requirement_id}`}>Locked until prerequisites</span>;
  if (done && r.open) return <span className="shrink-0 mt-0.5 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700" data-testid={`req-awaiting-${r.requirement_id}`}>Task done · awaiting verification</span>;
  return (
    <span className="shrink-0" data-testid={`req-complete-${r.requirement_id}`}>
      <TaskStatusSelect task={r.task} source="person_milestones" onChanged={onChanged} onBlocked={setBlocked} />
      <BlockedMsg msg={blocked} r={r} onClose={() => setBlocked(null)} />
    </span>
  );
}

function BlockedMsg({ msg, r, onClose }) {
  const navigate = useNavigate();
  return (
    <AlertDialog open={!!msg} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="bg-white" data-testid="req-complete-blocked">
        <AlertDialogHeader><AlertDialogTitle>{r.title}</AlertDialogTitle><AlertDialogDescription data-testid="req-complete-blocked-msg">{msg}</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Close</AlertDialogCancel>
          <AlertDialogAction onClick={() => navigate(`/tasks/detail/${r.task.id}`)} data-testid="req-complete-blocked-open">Open task</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ReqRow({ r, onViewAll, quick, onChanged }) {
  const navigate = useNavigate();
  const c = r.task?.completion;
  return (
    <li className={`flex flex-col sm:flex-row sm:items-start gap-2 rounded-lg border px-3 py-2.5 ${r.overdue || r.blocked ? "border-rose-200 bg-rose-50/40" : "border-slate-200 bg-white"}`}
      data-testid={`req-item-${r.requirement_id}`}>
      {quick && <QuickCheck r={r} onChanged={onChanged} />}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className={`text-sm font-medium ${r.open ? "text-slate-800" : "text-slate-500"}`}>{r.title}</p>
          <StatusPill status={r.status} />
          {r.overdue && <span className="text-[11px] font-semibold text-rose-700" data-testid="req-overdue">Overdue</span>}
        </div>
        {r.open ? (
          <p className="text-xs text-slate-500 mt-0.5">
            Action by <b className="font-medium text-slate-600">{r.action_owner_label}</b>
            {" · "}Responsible: <span className={r.responsible ? "text-slate-600" : "text-amber-700"}>{r.responsible || "Not assigned"}</span>
            {" · "}{r.due ? <span className={r.overdue ? "text-rose-700 font-medium" : ""}>Due {fmtDate(r.due)}</span> : "No due date"}
          </p>
        ) : (
          <p className="text-xs text-emerald-700 mt-0.5" data-testid={`req-completed-by-${r.requirement_id}`}>
            Completed {fmtDate((c?.at || r.task?.completed_at || r.completed_at || "").slice(0, 10)) || ""}{c?.by_name ? ` · by ${c.by_name}` : r.verified_by_name ? ` · by ${r.verified_by_name}` : ""}
            {r.responsible && c?.by_name && r.responsible !== c.by_name ? <span className="text-slate-400"> · Assigned to {r.responsible}</span> : null}
          </p>
        )}
        {r.missing.length > 0 && <p className="text-xs text-amber-700 mt-0.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{r.missing.join(" · ")}</p>}
      </div>
      {r.task ? (
        <button onClick={() => navigate(`/tasks/detail/${r.task.id}`)} className="shrink-0 text-xs font-medium text-brand hover:underline inline-flex items-center gap-1" data-testid={`req-open-task-${r.requirement_id}`}>
          <ClipboardList className="w-3.5 h-3.5" /> Open task
        </button>
      ) : onViewAll && (
        <button onClick={onViewAll} className="shrink-0 text-xs font-medium text-brand hover:underline" data-testid={`req-view-${r.requirement_id}`}>View in Milestones</button>
      )}
    </li>
  );
}

function group(list) {
  const groups = [];
  list.forEach((r) => {
    let g = groups.find((x) => x.name === r.category);
    if (!g) { g = { name: r.category, items: [] }; groups.push(g); }
    g.items.push(r);
  });
  return groups;
}

/** Outstanding requirements grouped by configured category (key-contact topic, else program phase). */
export function RequirementsList({ items, limit, onViewAll, quick = false, onChanged }) {
  const [showDone, setShowDone] = useState(false);
  const open = items.filter((r) => r.open).sort((a, b) => rank(a) - rank(b) || a.phase_order - b.phase_order);
  const done = quick ? items.filter((r) => !r.open && r.task?.status === "completed") : [];
  if (!items.length) return null;
  const shown = limit ? open.slice(0, limit) : open;
  return (
    <div className="space-y-4" data-testid="requirements-list">
      {!open.length && <p className="flex items-center gap-2 text-sm text-emerald-700" data-testid="req-all-done"><CheckCircle2 className="w-4 h-4" /> Every required qualification is verified complete.</p>}
      {group(shown).map((g) => (
        <div key={g.name}>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">{g.name}</p>
          <ul className="space-y-1.5">{g.items.map((r) => <ReqRow key={r.requirement_id} r={r} onViewAll={onViewAll} quick={quick} onChanged={onChanged} />)}</ul>
        </div>
      ))}
      {limit && open.length > shown.length && onViewAll && (
        <button onClick={onViewAll} className="text-sm font-medium text-brand hover:underline inline-flex items-center gap-1" data-testid="req-view-all">
          View All Requirements ({open.length} outstanding) <ArrowRight className="w-3.5 h-3.5" />
        </button>
      )}
      {done.length > 0 && (
        <div className="border-t border-slate-100 pt-3">
          <button onClick={() => setShowDone(!showDone)} className="text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-700" data-testid="req-toggle-completed">
            {showDone ? "Hide" : "Show"} completed requirements ({done.length})
          </button>
          {showDone && <ul className="space-y-1.5 mt-2" data-testid="req-completed-list">{done.map((r) => <ReqRow key={r.requirement_id} r={r} quick={quick} onChanged={onChanged} />)}</ul>}
        </div>
      )}
    </div>
  );
}
